import React from 'react';
import { Card, CardContent, CardHeader } from './ui/card';
import { SEOHead } from './SEOHead';
import { PageFaq, TextBlocks } from './PageAeo';
import { useSiteSettings, getPageSeo, buildFaqSchema } from '../services/SiteSettings';
import { SITE_URL } from '../data/organizationSchema.mjs';

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return dateString;
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
};

export function PrivacyPage() {
  const settings = useSiteSettings();
  const { privacy, contact } = settings;
  const seo = getPageSeo(settings, '/privacy');

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": `${SITE_URL}/` },
      { "@type": "ListItem", "position": 2, "name": "Privacy Policy", "item": `${SITE_URL}/privacy` }
    ]
  };
  const faqSchema = buildFaqSchema(seo.faqs);

  return (
    <div className="min-h-screen py-16">
      <SEOHead
        title={seo.title || 'Privacy Policy'}
        description={seo.description}
        canonical={`${SITE_URL}/privacy`}
        keywords={seo.keywords}
        structuredData={faqSchema ? [breadcrumbSchema, faqSchema] : breadcrumbSchema}
        modifiedTime={privacy.lastUpdated}
      />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <header className="text-center mb-16">
          <h1 className="mb-6">Privacy Policy</h1>
          {privacy.lastUpdated && (
            <p className="text-lg text-gray-600">
              Last Updated: <time dateTime={privacy.lastUpdated}>{formatDate(privacy.lastUpdated)}</time>
            </p>
          )}
        </header>

        <div className="space-y-8">
          {privacy.sections.map((section, i) => (
            <Card key={i}>
              <CardHeader>
                <h2 className="text-xl leading-none">{section.title}</h2>
              </CardHeader>
              <CardContent className="text-gray-600">
                <TextBlocks text={section.body} />
              </CardContent>
            </Card>
          ))}

          <Card>
            <CardHeader>
              <h2 className="text-xl leading-none">Contact Us</h2>
            </CardHeader>
            <CardContent className="space-y-4 text-gray-600">
              <p>If you have questions or comments about this Privacy Policy, please contact us at:</p>
              <address className="not-italic mt-4">
                <p>{contact.name}</p>
                <p>{contact.addressDisplay}</p>
                <p>Email: <a href={`mailto:${contact.email}`} className="hover:text-primary">{contact.email}</a></p>
                <p>Phone: {contact.phones.join(' / ')}</p>
              </address>
            </CardContent>
          </Card>
        </div>
      </div>

      <div className="mt-16">
        <PageFaq faqs={seo.faqs} />
      </div>
    </div>
  );
}
